
import { useEffect, useRef, useState } from 'react';

interface ScrollAnimationOptions {
  /** Percentage of the target's visibility the observer's callback should be executed */
  threshold?: number;
  /** Margin around the root. Can have values similar to CSS margin property */
  rootMargin?: string;
  /** If true, the observer will stop observing after the first intersection */
  once?: boolean;
}

/**
 * Hook that toggles a boolean when the referenced element enters/leaves the viewport.
 *
 * @param options IntersectionObserver options and behaviour flags.
 * @returns An object containing the `ref` to attach to the element and the `isVisible` flag.
 */
export function useScrollAnimation(options: ScrollAnimationOptions = {}) {
  const { threshold = 0.15, rootMargin = '0px 0px -60px 0px', once = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Wrap observer creation in a try/catch in case the browser does not support IntersectionObserver
    let observer: IntersectionObserver | null = null;
    try {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once && observer) {
              observer.unobserve(element);
            }
          } else if (!once) {
            setIsVisible(false);
          }
        },
        { threshold, rootMargin }
      );

      observer.observe(element);
    } catch (error) {
      console.error('IntersectionObserver is not supported or failed to initialize:', error);
      // Fallback: consider element visible to avoid UI dead‑lock
      setIsVisible(true);
    }

    // Cleanup
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
}

/**
 * Hook that reports visibility for a container that holds a staggered list of items.
 *
 * @param itemCount Number of items that will be animated (currently only used to trigger re‑run when it changes).
 * @param options IntersectionObserver options and behaviour flags.
 * @returns An object containing the `containerRef` and the `isVisible` flag.
 */
export function useStaggerAnimation(
  itemCount: number,
  options: ScrollAnimationOptions = {}
) {
  const { threshold = 0.1, rootMargin = '0px 0px -40px 0px', once = true } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    let observer: IntersectionObserver | null = null;
    try {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once && observer) {
              observer.unobserve(element);
            }
          } else if (!once) {
            setIsVisible(false);
          }
        },
        { threshold, rootMargin }
      );

      observer.observe(element);
    } catch (error) {
      console.error('IntersectionObserver error in useStaggerAnimation:', error);
      setIsVisible(true);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [threshold, rootMargin, once, itemCount]);

  return { containerRef, isVisible };
}
